import apiClient from './axios';

export interface AnimalGroup {
  id: string;
  name: string;
  species: 'tilapia' | 'catfish' | 'shrimp' | 'salmon' | 'trout' | 'other';
  batchNumber: string;
  stockingDate: string;
  initialCount: number;
  currentCount?: number;
  initialWeightGrams: number;
  currentAverageWeightGrams?: number;
  currentStage: 'fry' | 'fingerling' | 'juvenile' | 'sub_adult' | 'adult';
  mortalityRate?: number;
  notes?: string;
  farmId: string;
  farm?: {
    id: string;
    name: string;
  };
  pondId?: string;
  pond?: {
    id: string;
    name: string;
  };
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAnimalGroupDto {
  name: string;
  species: 'tilapia' | 'catfish' | 'shrimp' | 'salmon' | 'trout' | 'other';
  batchNumber: string;
  stockingDate: string;
  initialCount: number;
  initialWeightGrams: number;
  currentStage?: 'fry' | 'fingerling' | 'juvenile' | 'sub_adult' | 'adult';
  notes?: string;
  farmId: string;
}

export interface UpdateAnimalGroupDto extends Partial<CreateAnimalGroupDto> {
  currentAverageWeightGrams?: number;
  currentCount?: number;
  mortalityRate?: number;
  pondId?: string;
}

// Helper function to check if response is wrapped
function isWrappedResponse(responseData: any): boolean {
  return responseData && typeof responseData === 'object' && 'data' in responseData && 'success' in responseData;
}

export const fetchAnimalGroups = async (): Promise<AnimalGroup[]> => {
  try {
    const response = await apiClient.get('/animals/groups');
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (isWrappedResponse(responseData)) {
      return responseData.data || [];
    }
    
    // Fallback: check if responseData is directly an array
    if (Array.isArray(responseData)) {
      return responseData;
    }
    
    console.error('Invalid animal groups response format:', responseData);
    return [];
  } catch (error) {
    console.error('Error fetching animal groups:', error);
    return [];
  }
};

export const fetchAnimalGroupById = async (id: string): Promise<AnimalGroup | null> => {
  try {
    const response = await apiClient.get(`/animals/groups/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (isWrappedResponse(responseData)) {
      return responseData.data || null;
    }
    
    // Fallback: check if responseData is directly an AnimalGroup object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData;
    }
    
    console.error('Invalid animal group response format:', responseData);
    return null;
  } catch (error) {
    console.error('Error fetching animal group:', error);
    return null;
  }
};

export const createAnimalGroup = async (data: CreateAnimalGroupDto): Promise<AnimalGroup | null> => {
  try {
    const response = await apiClient.post('/animals/groups', data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (isWrappedResponse(responseData)) {
      return responseData.data || null;
    }
    
    // Fallback: check if responseData is directly an AnimalGroup object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData;
    }
    
    console.error('Invalid create animal group response format:', responseData);
    return null;
  } catch (error) {
    console.error('Error creating animal group:', error);
    throw error;
  }
};

export const updateAnimalGroup = async (id: string, data: UpdateAnimalGroupDto): Promise<AnimalGroup | null> => {
  try {
    const response = await apiClient.patch(`/animals/groups/${id}`, data);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (isWrappedResponse(responseData)) {
      return responseData.data || null;
    }
    
    // Fallback: check if responseData is directly an AnimalGroup object
    if (responseData && typeof responseData === 'object' && 'id' in responseData) {
      return responseData;
    }
    
    console.error('Invalid update animal group response format:', responseData);
    return null;
  } catch (error) {
    console.error('Error updating animal group:', error);
    throw error;
  }
};

export const deleteAnimalGroup = async (id: string): Promise<boolean> => {
  try {
    const response = await apiClient.delete(`/animals/groups/${id}`);
    
    // Handle wrapped response from TransformInterceptor
    const responseData = response.data;
    
    // Check if the response has the wrapped format
    if (isWrappedResponse(responseData)) {
      return responseData.success || false;
    }
    
    // Fallback: assume success if no error was thrown
    return true;
  } catch (error) {
    console.error('Error deleting animal group:', error);
    return false;
  }
}; 